import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { IMarketPositionDeal, IMarketPositionNode } from '../../types/EUMarkets.type';

export type MarketPositionNewState = {
  nodes: IMarketPositionNode[];
  dealsByPositionId: Record<string, IMarketPositionDeal[]>;
  expandedKeys: string[];
};

const initialState: MarketPositionNewState = {
  nodes: [],
  dealsByPositionId: {},
  expandedKeys: [],
};

const marketPositionNew = createSlice({
  name: 'MarketPositionNew',
  initialState,
  reducers: {
    mergeNodes: (state, action: PayloadAction<IMarketPositionNode[]>) => {
      // sourceId is only unique within a parent, so scope the dedup key by parent as well —
      // otherwise sibling nodes under different parents that reuse a sourceId (e.g. Locations)
      // silently overwrite each other and disappear from the tree.
      const buildKey = (node: IMarketPositionNode) =>
        `${node.parentNodeId}:${node.parentSourceId}:${node.nodeId}:${node.sourceId}:${node.category}`;
      const nodesByKey = new Map(state.nodes.map((node) => [buildKey(node), node]));
      action.payload.forEach((node) => {
        nodesByKey.set(buildKey(node), node);
      });
      state.nodes = Array.from(nodesByKey.values());
    },
    setPositionDeals: (
      state,
      action: PayloadAction<{ positionId: number; deals: IMarketPositionDeal[] }>,
    ) => {
      state.dealsByPositionId[String(action.payload.positionId)] = action.payload.deals;
    },
    setExpandedKeys: (state, action: PayloadAction<string[]>) => {
      state.expandedKeys = action.payload;
    },
    clear: () => initialState,
  },
});

export const { mergeNodes, setExpandedKeys, setPositionDeals, clear } = marketPositionNew.actions;
export default marketPositionNew.reducer;
